/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

import React, {forwardRef, useCallback, useEffect, useState} from 'react';
import NumberFormat from 'react-number-format';
import {Grid, IconButton, makeStyles, Popover} from '@material-ui/core';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import ArrowDropUp from '@material-ui/icons/ArrowDropUp';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import classnames from 'classnames';
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {Props} from '../../components/textfield/TextField';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import {CTALink} from '../buttons/CTALink';
import {ClickAwayListener} from '../listeners/ClickAwayListener';
import FormTextField from './FormTextField';

// defines the props definition for FormNumberField...

export interface FormNumberFieldProp extends Props {
  thousandSeparator?: boolean;
  isNumericString?: boolean;
  suffix?: string;
  prefix?: string;
  min?: number;
  max?: number;
  decimalScale?: number;
  includeStepper?: boolean;
  allowedRange?: boolean;
  includeStepPicker?: boolean;
  includeCTAs?: boolean;
};

interface StyleProps {
  showPicker?: boolean;
};

export interface StepPickerProps {
  max?: number;
  min?: number;
  steps?: number;
  classes: GenericObject;
  onClose: Function;
  anchorEl: any;
  defaultValue?: number;
  includeCTAs?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        easing: {
          standard: standardEasing
        },
        duration: {
          t2: motionDuration
        }
      }
    }
  } = theme;
  return {
    root : {
      display        : 'flex',
      flexDirection  : 'row',
      width          : '100%',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    stepper : {
      display       : 'flex',
      flexDirection : 'column',
      gap           : '2px',
      position      : 'relative',
      right         : '8px'
    },
    arrow : {
      height          : '20px',
      fontSize        : '20px',
      backgroundColor : dlsColor('grey8'),
      '&:hover'       : {
        cursor          : 'pointer',
        backgroundColor : dlsColor('lightBlue')
      }
    },
    upArrow : {
      borderRadius : '5px 5px 0 0',
      border       : `0.5px solid ${dlsColor('grey5')}`
    },
    downArrow : {
      borderRadius : '0 0 5px 5px',
      border       : `0.5px solid ${dlsColor('grey5')}`
    },
    pickerArrow : {
      borderRadius : '50%',
      border       : `0.5px solid ${dlsColor('grey5')}`,
      transform    : ({showPicker}: StyleProps) => showPicker ? 'rotateZ(180deg)' : 'unset',
      transition   : `transform ${motionDuration} ${standardEasing}`
    },
    popup : {
      position                       : 'unset',
      margin                         : '8px',
      width                          : '128px',
      right                          : 0,
      [theme.breakpoints.down('sm')] : {
        position : 'unset'
      }
    },
    container : {
      display       : 'flex',
      flexDirection : 'column',
      alignItems    : 'center',
      padding       : '20 16 20 16'
    },
    iconTop : {
      '&:hover' : {
        background : 'none'
      },
      transform : 'rotate(90deg)'
    },
    cta : {
      color     : dlsColor('grey3'),
      '&:hover' : {
        color : dlsColor('caBlue')
      },
      '&:select' : {
        color : dlsColor('caBlue')
      },
      cursor : 'pointer',
      ...dlsAlt('typography', 'cta2')
    },
    iconBottom : {
      '&:hover' : {
        background : 'none'
      },
      transform : 'rotate(270deg)'
    },
    pickerValue : {
      display    : 'flex',
      alignItems : 'center',
      textAlign  : 'center',
      fontFamily : 'Neo Sans Std',
      fontWeight : 400,
      fontSize   : '30px',
      lineHeight : '36px',
      padding    : '20px 0 20px 0'
    },
    containerRoot : {
      display        : 'flex',
      flexDirection  : 'row',
      justifyContent : 'start',
      alignItems     : 'center',
      position       : 'relative'
    }
  };
});

const StepPicker = ({
  max,
  min,
  steps,
  classes,
  onClose,
  anchorEl,
  defaultValue,
  includeCTAs = false
}: StepPickerProps) => {
  const [value, setValue] = useState(defaultValue);

  const onUp = useCallback(() => {
    const val = Number(value ?? min ?? 0) + steps;
    setValue(val > max ? min : val);
  }, [value, min, max, steps]);

  const onDown = useCallback(() => {
    const val = Number(value ?? max ?? 0) - steps;
    setValue(val < min ? max : val);
  }, [value, max, min, steps]);

  const onCancel = useCallback((e) => {
    onClose(e, value, false);
  }, [onClose, value]);

  const onDone = useCallback((e) => {
    onClose(e, value, true);
  }, [onClose, value]);

  return (
    <Popover anchorEl={anchorEl}
      style={{marginLeft: '10px'}}
      anchorReference='anchorEl'
      anchorOrigin={{
        vertical   : 'bottom',
        horizontal : 'right'
      }}
      transformOrigin={{
        vertical   : 'top',
        horizontal : 'right'
      }}
      open>
      <ClickAwayListener onClickAway={includeCTAs ? onCancel : onDone} mountListener>
        <div className={classes.popup}>
          <Grid container>
            <Grid container>
              <Grid item
                container
                direction='row'
                style={{marginBottom: '24px'}}
                justifyContent='center'>
                <div className={classes.containerRoot}>
                  <div className={classes.container}>
                    <IconButton className={classes.iconTop}
                      onClick={onUp}
                      size='small'>
                      <ChevronLeft />
                    </IconButton>
                    <span className={classes.pickerValue}>
                      {value}
                    </span>
                    <IconButton className={classes.iconBottom}
                      onClick={onDown}
                      size='small'>
                      <ChevronLeft />
                    </IconButton>
                  </div>
                </div>
              </Grid>
              {includeCTAs && (
                <Grid item
                  container
                  direction='row'
                  justifyContent='space-evenly'>
                  <CTALink className={classes.cta} onClick={onCancel}>
                    {translate('common:editors.shared.cancel')}
                  </CTALink>
                  <CTALink className={classes.cta} onClick={onDone}>
                    {translate('common:editors.shared.done')}
                  </CTALink>
                </Grid>
              )}
            </Grid>
          </Grid>
        </div>
      </ClickAwayListener>
    </Popover>
  );
};

// defines the NumberFormatCustom for custom number formatting...
const NumberFormatCustom = forwardRef(
  (props: any, ref) => {
    const {
      onChange,
      name,
      min,
      max,
      defaultValue,
      decimalScale,
      value: clientValue,
      steps = 1,
      includeCTAs = false,
      includeStepPicker = false,
      includeStepper = false,
      allowedRange = false,
      thousandSeparator = true,
      isNumericString = false,
      ...other
    } = props;
    const [value, setValue]           = useState(defaultValue);
    const [showPicker, setShowPicker] = useState(false);
    const [anchorEl, setAnchorEl]     = useState<null | HTMLElement>(null);
    const classes                     = useStyles({showPicker});

    useEffect(() => {
      if (!isNil(clientValue) && clientValue !== value) {
        setValue(clientValue);
      }
    }, [clientValue]);

    const handleChange = useCallback(function({value}) {
      const isValidChange = (
        !allowedRange || isNil(value) ||
        value === '' || (min <= value && max >= value)
      );
      if (isValidChange) {
        const error  = (
          (!isUndefined(min) && value < min) ||
          (!isUndefined(max) && value > max)
        );
        const target = {
          value,
          name,
          error
        };
        onChange?.({
          currentTarget : target,
          target
        });
        setValue(value);
      }
    }, [onChange, min, max, name, allowedRange, onChange]);

    const onUp = useCallback(() => {
      const val = Number(value ?? min ?? 0) + steps;
      handleChange({value: val > max ? min : val});
    }, [value, handleChange, min, max, steps]);

    const onDown = useCallback(() => {
      const val = Number(value ?? max ?? 0) - steps;
      handleChange({value: val < min ? max : val});
    }, [value, handleChange, max, min, steps]);

    const onStepperArrowClick = useCallback((event) => {
      if (!showPicker) {
        event?.preventDefault();
        event?.stopPropagation();
        setAnchorEl(event?.currentTarget);
        setShowPicker(true);
      }
      event?.target?.blur?.();
    }, [showPicker]);

    const closePicker = useCallback((event, value, byDone) => {
      event?.preventDefault();
      event?.stopPropagation();
      setShowPicker(false);
      setAnchorEl(null);
      if (byDone) {
        handleChange({value});
      }
    }, [handleChange]);

    return (
      <div className={classes.root}
        onClick={includeStepPicker ? onStepperArrowClick : null}>
        <NumberFormat {...other}
          getInputRef={ref}
          value={value}
          onValueChange={handleChange}
          thousandSeparator={thousandSeparator}
          isNumericString={isNumericString}
          decimalScale={decimalScale} />
        {includeStepper && (
          <div className={classes.stepper}>
            <div className={classnames(classes.arrow, classes.upArrow)}
              onClick={onUp}>
              <ArrowDropUp fontSize='inherit' />
            </div>
            <div className={classnames(classes.arrow, classes.downArrow)}
              onClick={onDown}>
              <ArrowDropDown fontSize='inherit' />
            </div>
          </div>
        )}
        {includeStepPicker && (
          <>
            <div className={classes.stepper}>
              <div className={classnames(classes.arrow, classes.pickerArrow)}
                onClick={onStepperArrowClick}>
                <ArrowDropUp fontSize='inherit' />
              </div>
            </div>
            {showPicker && (
              <StepPicker defaultValue={value}
                max={max}
                min={min}
                steps={steps}
                includeCTAs={includeCTAs}
                onClose={closePicker}
                anchorEl={anchorEl}
                classes={classes} />
            )}
          </>
        )}
      </div>
    );
  }
);

/**
 * Component that wraps the TextField with styles that are specific to the
 * a Form by pass through props that get sent to TextField
 *
 * ```typescript
 *
 * import {FormNumberField} from '@ion/dls/src/components/forms/';
 *
 * <FormNumberField {...props} suffix='%' prefix='$' />
 *
 * ```
 */

export const FormNumberField = ({
  ...props
}: FormNumberFieldProp) => (
  <FormTextField {...props}
    InputProps={{
      inputComponent : NumberFormatCustom as any,
      ...(props?.InputProps || {})
    }}
    inputProps={{
      onChange          : props?.onChange,
      onBlur            : props?.onBlur,
      name              : props?.name,
      thousandSeparator : props?.thousandSeparator,
      isNumericString   : props?.isNumericString,
      maxLength         : props?.maxLength,
      min               : props?.min,
      max               : props?.max,
      value             : props?.value,
      decimalScale      : props?.decimalScale,
      includeStepper    : props?.includeStepper,
      allowedRange      : props?.allowedRange,
      includeStepPicker : props?.includeStepPicker,
      includeCTAs       : props?.includeCTAs,
      ...props?.inputProps
    }} />
);

export default FormNumberField;
